<template>
    <validation-observer v-slot="{ invalid, handleSubmit }" ref="degreeValidator">
        <form @submit.prevent="handleSubmit(submit)" @keydown="errors.clear($event.target.name)">

            <base-input class="mb-3"
                :label="$t('common.name')"
                label-classes="form-control-label required"
                :placeholder="$t('common.name')"
                v-model="value.name"
                name="name"
                rules="required"
                :error="errors.get('name')">
            </base-input>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('common.code')"
                label-classes="form-control-label required"
                :placeholder="$t('common.code')"
                v-model="value.code"
                name="code"
                rules="required"
                :error="errors.get('code')">
            </base-input>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.language')"
                :placeholder="$t('common.language')"
                label-classes="required"
                v-model="value.lang"
                :options="langs"
                @input="errors.clear('lang')"
                :error="errors.get('lang')"
                :disabled="value.id !== null"
            >
            </base-select>

            <div class="mb-3 mt--4" v-if="value.id == null">
                <small><span class="text-warning">
                    {{ $t('common.not-allowed-to-change') }}
                </span></small>
            </div>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.position') + ' 1'"
                :placeholder="$t('common.position') + ' 1'"
                label-classes="required"
                v-model="value.simulation_role_1"
                :options="simulationRolesLang('en')"
                :notes="simulationRolesLang('fr')"
                @input="errors.clear('simulation_role_1')"
                :error="errors.get('simulation_role_1')"
                :disabled="value.id !== null"
            >
            </base-select>

            <div class="mb-3 mt--4" v-if="value.id == null">
                <small><span class="text-warning">
                    {{ $t('common.not-allowed-to-change') }}
                </span></small>
            </div>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.position') + ' 2'"
                :placeholder="$t('common.position') + ' 2'"
                v-model="value.simulation_role_2"
                :options="simulationRolesLang('en')"
                :notes="simulationRolesLang('fr')"
                @input="errors.clear('simulation_role_2')"
                :error="errors.get('simulation_role_2')"
                :disabled="value.id !== null"
                clearable
            >
            </base-select>

            <div class="mb-3 mt--4" v-if="value.id == null">
                <small><span class="text-warning">
                    {{ $t('common.not-allowed-to-change') }}
                </span></small>
            </div>

            <base-select class="mb-4" v-if="!duplicating && Object.keys(ratingScales).length > 1"
                :label="$t('competencies.rating-scale')"
                :placeholder="$t('competencies.rating-scale')"
                v-model="value.rating_scale"
                :options="ratingScales"
                @input="errors.clear('rating_scale')"
                :error="errors.get('rating_scale')"
                :disabled="published"
            >
            </base-select>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('degrees.main-notification')"
                :placeholder="$t('degrees.notification-field')"
                v-model="value.main_notification"
                name="main_notification"
                :error="errors.get('main_notification')">
            </base-input>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('degrees.gppos-notification')"
                :placeholder="$t('degrees.notification-field')"
                v-model="value.gppos_notification"
                name="gppos_notification"
                :error="errors.get('gppos_notification')">
            </base-input>

            <base-checkbox class="mb-3" v-if="!duplicating"
                v-model="value.go_ground"
                :disabled="published"
            >
                {{ $t('degrees.go-ground') }}
            </base-checkbox>

            <base-checkbox class="mb-3" v-if="!duplicating"
                v-model="value.shared"
            >
                {{ $t('common.shared') }}
            </base-checkbox>

            <base-select class="mb-4" v-if="!duplicating && !$auth.user.owner_id"
                :label="$t('common.owner')"
                :placeholder="$t('common.owner')"
                v-model="value.owner_id"
                :options="owners"
                @input="errors.clear('owner_id')"
                :error="errors.get('owner_id')"
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating && value.status != 'active' && value.status != 'archived'"
                :label="$t('common.status')"
                :placeholder="$t('common.status')"
                v-model="value.status"
                :options="statusOptions"
                :sort="false"
            >
            </base-select>

            <div class="category form-category mb-4">
                <small><span class="text-warning">*</span> {{ $t('common.required-fields') }}</small>
            </div>

            <div class="text-center" v-if="duplicating">
                <base-button native-type="submit"
                    type="primary"
                    :disabled="errors.any() || invalid"
                >
                    {{ value.id ? (duplicating ? $t('common.duplicate') : $t('common.update')) : $t('common.create') }}
                </base-button>

                <base-button type="link" @click="modal.show = false">
                    {{ $t('common.cancel') }}
                </base-button>
            </div>

            <div class="text-center" v-if="!duplicating">
                <base-button native-type="submit"
                    type="primary"
                    :disabled="errors.any() || invalid"
                >
                    {{ value.id ? (duplicating ? $t('common.duplicate') : $t('common.update')) : $t('common.create') }}
                </base-button>

                <base-button outline 
                    type="primary"
                    :disabled="errors.any() || invalid"
                    @click="next"
                >
                    {{ $t('common.more') }}
                </base-button>
            </div>

            <div class="text-center mt-3" v-if="!duplicating">
                <base-button type="link" @click="modal.show = false">
                    {{ $t('common.cancel') }}
                </base-button>
            </div>

        </form>
    </validation-observer>
</template>

<script>
    export default {

        props: {

            value: {
                type: Object,
                required: true
            },

            errors: {
                type: Object,
                required: true
            },

            modal: {
                type: Object,
                default: null
            },

            duplicating: {
                type: Boolean,
                default: false
            },

            statusOptions: {
                type: Object,
                required: true
            },

            langs: {
                type: Object,
                required: true
            },

            simulationRoles: {
                type: Object,
                required: true
            },

            ratingScales: {
                type: Object,
                required: true
            },

            owners: {
                type: Object,
                required: true
            },
        },

        created() {
            this.errors.setValidator('degreeValidator', this)
        },

        watch: {

            duplicating(val) {
                if (this.duplicating) {
                    this.value.name += ' (copy)'
                }
            }
        },

        computed: {

            published() {
                return this.value.status == 'active'
            }
        },

        methods: {
            
            simulationRolesLang(lang) {
                let roles = {}
                for (let id in this.simulationRoles) {
                    roles[id] = this.simulationRoles[id][lang]
                }
                return roles
            },

            submit() {
                if (this.duplicating) {
                    this.$loader.show()
                }
                this.$emit('input', { ...this.value})
            },

            next() {
                this.$emit('next')
                this.submit()
            }
        }
    }
</script>
